import { useZero } from "@rocicorp/zero/react";
import { useCallback, useEffect, useRef } from "react";
import { sharedMutators } from "../../../shared/mutators";
import type {
  MonitoringAnalytics,
  MonitoringNavSource,
  MonitoringTab,
} from "../../../shared/mutators/monitoringAnalytics";
import { useCurrentLocation } from "../../lib/urls";
import { isTelemetryActive, sessionId } from "../../lib/tracing";

export type MonitoringInteractionName =
  | "workflows.sort_change"
  | "workflows.route_sort_change"
  | "workflows.panel_drilldown_open"
  | "workflows.workflow_link_click"
  | "workflows.space_link_click"
  | "workflows.storage_modal_open"
  | "workflows.dependencies_view_toggle"
  | "workflows.duplicate_decision"
  | "workflows.duplicate_refresh"
  | "access.node_click"
  | "access.create_menu_action"
  | "access.context_menu_action"
  | "value.window_change"
  | "value.space_filter_change"
  | "value.table_sort"
  | "value.workflow_value_modal_open"
  | "ai.provider_filter_change"
  | "ai.model_table_sort"
  | "ai.spend_vs_limits_open"
  | "connectors.window_change"
  | "connectors.connector_filter_change"
  | "connectors.space_filter_change"
  | "connectors.table_sort"
  | "connectors.workflow_table_sort"
  | "surface.hidden";

const TAB_BY_SEGMENT: Record<string, MonitoringTab> = {
  workflows: "workflows",
  access: "access",
  value: "value",
  ai: "ai",
  connectors: "connectors",
};

function currentTab(): MonitoringTab | null {
  const [, root, segment] = location.pathname.split("/");
  if (root !== "monitoring" || !segment) return null;
  return TAB_BY_SEGMENT[segment] ?? null;
}

// The visit outlives every component under /monitoring: each tab renders its
// own MonitoringLayout, so switching tabs remounts the whole subtree and
// component state would mint a fresh id on every switch.
let visitId: string | null = null;
let priorTab: MonitoringTab | null = null;
// Counted by the tab-view effect alone, so "first view of this visit" can't be
// confused by anything else minting the visit id first.
let viewsInVisit = 0;
// Read at module init, which is app boot: a first visit that matches the URL
// the document opened on was reached by deep link rather than in-app nav. URL
// normalization replaces history entries during render, so the visited-location
// log can't answer this by the time the tab-view effect runs.
const bootPathname = location.pathname;
let hadVisit = false;

function startedVisit(): string {
  visitId ??= crypto.randomUUID();
  return visitId;
}

function endVisit() {
  visitId = null;
  priorTab = null;
  viewsInVisit = 0;
}

function otelSessionId(): string | null {
  return isTelemetryActive() ? sessionId : null;
}

function urlParam(name: string): string | null {
  return new URLSearchParams(location.search).get(name);
}

// Number(null) is 0 and 0 is finite, so an absent param has to be rejected
// before parsing or it reports as a real window of zero days.
function urlNumberParam(name: string): number | null {
  const raw = urlParam(name);
  if (raw === null) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

type Zero = ReturnType<typeof useZero>;

function emitInteraction(
  z: Zero,
  tab: MonitoringTab,
  interactionName: MonitoringInteractionName,
  detail?: { targetType?: string; targetId?: string; value?: string }
) {
  z.mutate(
    sharedMutators.recordMonitoringInteraction({
      tab,
      visitId: startedVisit(),
      interactionName,
      targetType: detail?.targetType ?? null,
      targetId: detail?.targetId ?? null,
      value: detail?.value ?? null,
    })
  );
}

/**
 * Emits a tab view on every route match under /monitoring, plus one on return
 * from a background tab and a `surface.hidden` marker on the way out — the
 * pair is what lets the warehouse subtract background time from dwell rather
 * than capping it. Mounted once, in the component that spans every tab.
 */
export function useMonitoringTabViews() {
  const z = useZero();
  // Keyed on the tab, not the location: sort and window controls (and the URL
  // normalization inside useCurrentUrl) rewrite the query string without
  // leaving the tab, and re-running there would emit a duplicate view.
  useCurrentLocation();
  const tab = currentTab();
  // The bare /monitoring path renders RedirectToWorkflows, which replaces to
  // the Workflows tab in an effect; emitting there would produce a tab-less
  // ghost view followed by a duplicate, so it only colors the landing view.
  const cameViaRedirect = useRef(false);

  useEffect(() => {
    if (!tab) {
      cameViaRedirect.current = true;
      return;
    }

    const isFirstOfVisit = viewsInVisit === 0;
    const isDeepLink =
      isFirstOfVisit && !hadVisit && bootPathname.startsWith("/monitoring");
    const navSource: MonitoringNavSource = cameViaRedirect.current
      ? "redirect"
      : !isFirstOfVisit
        ? "tab_switch"
        : isDeepLink
          ? "deep_link"
          : "nav";
    cameViaRedirect.current = false;
    hadVisit = true;

    z.mutate(
      sharedMutators.markMonitoringTabViewed({
        tab,
        visitId: startedVisit(),
        navSource,
        priorTab,
        urlWindow: urlNumberParam("window"),
        urlSort: urlParam("sort"),
        urlRouteSort: urlParam("routeSort"),
        otelSessionId: otelSessionId(),
      })
    );
    priorTab = tab;
    viewsInVisit++;
  }, [z, tab]);

  useEffect(() => {
    const onVisibilityChange = () => {
      const tab = currentTab();
      if (!tab) return;
      if (document.visibilityState === "hidden") {
        emitInteraction(z, tab, "surface.hidden");
        return;
      }
      z.mutate(
        sharedMutators.markMonitoringTabViewed({
          tab,
          visitId: startedVisit(),
          navSource: "visibility_return",
          // Resuming a backgrounded tab is not a tab change, so there is no
          // prior tab; the module-level one is still this tab.
          priorTab: null,
          urlWindow: urlNumberParam("window"),
          urlSort: urlParam("sort"),
          urlRouteSort: urlParam("routeSort"),
          otelSessionId: otelSessionId(),
        })
      );
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [z]);

  useEffect(() => endVisit, []);
}

/** Records an in-page interaction that changes no server state. */
export function useMonitoringInteraction() {
  const z = useZero();

  return useCallback(
    (
      interactionName: MonitoringInteractionName,
      detail?: { targetType?: string; targetId?: string; value?: string }
    ) => {
      const tab = currentTab();
      if (!tab) return;
      emitInteraction(z, tab, interactionName, detail);
    },
    [z]
  );
}

/**
 * Builds the `analytics` argument to thread onto a mutation Monitoring shares
 * with another surface, so the warehouse can tell the two firings apart.
 * Returns a function rather than a value: resolving it at render time would
 * mint the visit before the tab-view effect had run, and would freeze the tab
 * a handler reports into whatever it was when the component last rendered.
 */
export function useMonitoringAnalytics() {
  return useCallback((): MonitoringAnalytics | undefined => {
    const tab = currentTab();
    if (!tab) return undefined;
    return { visitId: startedVisit(), tab };
  }, []);
}
